import { snakeCaseDictToCamelCaseDict } from '../utils';
import { IUser } from '../types';
import axios from 'axios';
import { baseUrl } from '../axios';
import { createPartnerApiInterceptor, partnerSentryConfig } from './sentry';

const axiosInstancePdv = axios.create({
  baseURL: baseUrl + '/v1',
  timeout: 30000, 
});

const axiosInstancePdv2 = axios.create({
  baseURL: baseUrl + '/v2',
  timeout: 30000,
});

// Interceptors para adicionar token automaticamente
axiosInstancePdv.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('partner_token');
    if (token) {
      config.headers.Authorization = `TOKEN ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstancePdv2.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('partner_token');
    if (token) {
      config.headers.Authorization = `TOKEN ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Aplicar interceptors do Sentry
createPartnerApiInterceptor(axiosInstancePdv);
createPartnerApiInterceptor(axiosInstancePdv2);

// Interceptors para lidar com respostas de erro (token expirado)
axiosInstancePdv.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      partnerSentryConfig.captureAuthError(error, error.config?.url || 'unknown');
      localStorage.removeItem('partner_token');
      localStorage.removeItem('partner_user');
      window.location.href = '/partner/login';
    }
    return Promise.reject(error);
  }
);

axiosInstancePdv2.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      partnerSentryConfig.captureAuthError(error, error.config?.url || 'unknown');
      localStorage.removeItem('partner_token');
      localStorage.removeItem('partner_user');
      window.location.href = '/partner/login';
    }
    return Promise.reject(error);
  }
);

export interface IPartnerUser extends IUser {
  isPartner: boolean;
  companyId?: number;
}

export interface IPartnerLoginResponse {
  token: string;
  isNewUser: boolean;
  user: IPartnerUser;
  store?: IStoreData;
  company?: ICompanyResponse;
}

// Interface simplificada para registro apenas do usuário
export interface IPartnerUserRegistrationData {
  full_name: string;
  email: string;
  password: string;
}

export interface ICompanyExistsResponse {
  exists: boolean;
  companyId?: number;
  dashboardLink?: string;
  message?: string;
}

export interface IStoreData {
  postal_code: string;
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  state: string;
  city: string;
  country: string;
}

export interface ICompanyData {
  cnpj: string;
  owner: string;
  email: string;
  corporate_name: string;
  name: string;
  category: string;
  description: string;
  cellphone: string;
  site?: string;
}

export interface ICompanyRegistrationData {
  company: ICompanyData;
  exclusive?: boolean;
  cover_photo?: File;
  logo?: File;
  store: IStoreData;
}

export interface ICompanyResponse {
  id: number;
  cnpj: string;
  corporate_name: string;
  name: string;
  category: string;
  description: string;
  cellphone: string;
  site?: string;
  cover_photo?: string;
  logo?: string;
  store: IStoreData;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
}

export interface IDiscountRules {
  cumulativa?: boolean;
  delivery?: boolean;
  promocao?: boolean;
  disponibilidade?: boolean;
  outros?: boolean;
  outrosText?: string;
}
export interface IDiscountData {
  discount: string;
  name: string;
  stores: number;
  company: number;
  link?: string;
  coupon?: string;
  rules: string | IDiscountRules;
  mode: 'local' | 'online';
  type: 'percentage' | 'fixed' | 'cupom';
  image?: File;
}

export interface IDiscountResponse {
  id: number;
  discount: string;
  title_discount: string;
  link_desconto?: string;
  cupom_desconto?: string;
  rules: string;
  mode: 'local' | 'online';
  type: 'percentage' | 'fixed' | 'cupom';
  image?: string;
  company_id: number;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
}

// Registrar uma nova empresa
export async function registerCompany(data: ICompanyRegistrationData, agreementId: string): Promise<{
  store: IStoreData;
  company: ICompanyResponse;
}> {
  const formData = new FormData();

  // Adicionar dados básicos
  formData.append('company.cnpj', data.company.cnpj.replace(/\D/g, ''));
  formData.append('company.corporate_name', data.company.corporate_name);
  formData.append('company.name', data.company.name);
  formData.append('company.category', data.company.category);
  formData.append('company.description', data.company.description);
  formData.append('company.cellphone', data.company.cellphone.replace(/\D/g, ''));

  formData.append('creator_agreement', agreementId);

  if (data.exclusive) formData.append('agreement', agreementId);

  formData.append('company.owner', data.company.owner);
  formData.append('company.email', data.company.email);

  if (data.company.site) formData.append('company.site', data.company.site);
  if (data.cover_photo) formData.append('cover_photo', data.cover_photo);
  if (data.logo) formData.append('logo', data.logo);

  // Adicionar dados da loja (opcionais)
  if (data.store.postal_code) formData.append('store.postal_code', data.store.postal_code.replace(/\D/g, ''));
  if (data.store.street) formData.append('store.street', data.store.street);
  if (data.store.number) formData.append('store.number', data.store.number);
  if (data.store.complement) formData.append('store.complement', data.store.complement);
  if (data.store.neighborhood) formData.append('store.neighborhood', data.store.neighborhood);
  if (data.store.state) formData.append('store.state', data.store.state);
  if (data.store.city) formData.append('store.city', data.store.city);
  if (data.store.country) formData.append('store.country', data.store.country);

  try {
    const response = await axiosInstancePdv.post('/store_company_register/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    const { data: companyData } = await axiosInstancePdv.get('/user/pdv_company/');

    return {
      store: companyData.store as IStoreData,
      company: companyData.company as ICompanyResponse,
    };
  } catch (error: any) {
    // Capturar erros específicos de validação
    if (error.response?.status === 400 && error.response?.data) {
      const validationErrors = error.response.data;
      
      // Capturar erros de campos específicos
      Object.keys(validationErrors).forEach(field => {
        if (Array.isArray(validationErrors[field])) {
          partnerSentryConfig.captureValidationError(
            field,
            validationErrors[field],
            '/store_company_register/'
          );
        }
      });
    }

    // Capturar erros de upload de arquivos
    if (data.cover_photo || data.logo) {
      partnerSentryConfig.captureFileUploadError(
        error,
        data.cover_photo?.name || data.logo?.name || 'unknown',
        '/store_company_register/'
      );
    }

    // Re-throw o erro para ser tratado pelo hook
    throw error;
  }
}

// Registrar desconto
export async function registerDiscount(data: IDiscountData): Promise<IDiscountResponse> {
  const formData = new FormData();

  formData.append('mode', data.mode);
  formData.append('type', data.type);
  formData.append('discount', data.discount);
  formData.append('name', data.name);
  formData.append('rules', JSON.stringify(data.rules));

  formData.append('company', data.company.toString());

  formData.append('stores', data.stores.toString());

  if (data.link) formData.append('link', data.link);
  if (data.coupon) formData.append('coupon', data.coupon);
  if (data.image) formData.append('image', data.image);

  try {
    const response = await axiosInstancePdv2.post('/promotion/pdv/promotions/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return snakeCaseDictToCamelCaseDict(response.data) as IDiscountResponse;
  } catch (error: any) {
    // Capturar erros específicos de validação
    if (error.response?.status === 400 && error.response?.data) {
      const validationErrors = error.response.data;
      
      // Capturar erros de campos específicos
      Object.keys(validationErrors).forEach(field => {
        if (Array.isArray(validationErrors[field])) {
          partnerSentryConfig.captureValidationError(
            field,
            validationErrors[field],
            '/promotion/pdv/promotions/'
          );
        }
      });
    }

    // Capturar erros de upload de arquivos
    if (data.image) {
      partnerSentryConfig.captureFileUploadError(
        error,
        data.image.name || 'unknown',
        '/promotion/pdv/promotions/'
      );
    }

    // Re-throw o erro para ser tratado pelo hook
    throw error;
  }
}

// Obter descontos da empresa
export async function getCompanyDiscounts(): Promise<IDiscountResponse[]> {
  return axiosInstancePdv2.get(`/promotion/pdv/promotions/`).then(({ data }) => data);
}

// Obter empresa
export async function getCompany(): Promise<ICompanyResponse> {
  return axiosInstancePdv.get(`/user/pdv_company/`).then(({ data }) => data);
}

// Atualizar desconto
export async function updateDiscount(discountId: number, data: Partial<IDiscountData>): Promise<IDiscountResponse> {
  const formData = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    if (key === 'rules' && typeof value === 'object') {
      // Regras do desconto
      Object.entries(value).forEach(([ruleKey, ruleValue]) => {
        if (ruleValue !== undefined && ruleValue !== null) {
          formData.append(`rules.${ruleKey}`, ruleValue.toString());
        }
      });
    } else if (value instanceof File) {
      formData.append(key, value);
    } else if (value !== undefined && value !== null) {
      formData.append(key, value.toString());
    }
  });

  return axiosInstancePdv
    .patch(`/partners/discount/${discountId}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(({ data }) => snakeCaseDictToCamelCaseDict(data) as IDiscountResponse);
}

// Login de parceiro
export async function partnerLogin({
  email,
  password,
}: {
  email: string;
  password: string;
}): Promise<IPartnerLoginResponse> {
  try {
    const response = await axiosInstancePdv
      .post('/users/tokens/', {
        username: email,
        password,
        partner: true,
      })
      .then(({ data }) => ({
        token: data.token,
        isNewUser: false,
      }));

    // Salvar token no localStorage
    localStorage.setItem('partner_token', response.token);

    const { data: userData } = await axiosInstancePdv.patch('/profile/my/', {});

    try {
      const { data: companyData } = await axiosInstancePdv.get('/user/pdv_company/');

      const loginResponse = {
        token: response.token,
        isNewUser: false,
        user: snakeCaseDictToCamelCaseDict(userData) as IPartnerUser,
        store: snakeCaseDictToCamelCaseDict(companyData.store) as IStoreData,
        company: snakeCaseDictToCamelCaseDict(companyData.company) as ICompanyResponse,
      };

      // Salvar dados do usuário no localStorage
      localStorage.setItem('partner_user', JSON.stringify(loginResponse.user));

      return loginResponse;
    } catch (error) {
      const loginResponse = {
        token: response.token,
        isNewUser: false,
        user: userData as IPartnerUser,
      };

      // Salvar dados do usuário no localStorage
      localStorage.setItem('partner_user', JSON.stringify(loginResponse.user));

      return loginResponse;
    }
  } catch (error: any) {
    // Capturar erros de autenticação
    if (error.response?.status === 400 || error.response?.status === 401) {
      partnerSentryConfig.captureAuthError(error, '/users/tokens/');
    }
    // Re-throw o erro para ser tratado pelo hook
    throw error;
  }
}

// Registro de parceiro (apenas usuário)
export async function partnerRegister(
  data: IPartnerUserRegistrationData,
  agreementId: string
): Promise<IPartnerLoginResponse> {
  try {
    const response = await axiosInstancePdv
      .post(`/profile/?agreement_id=${agreementId}`, data)
      .then(({ data: responseData }) => ({
        token: responseData.token,
      }));

    // Salvar token no localStorage
    localStorage.setItem('partner_token', response.token);

    const { data: userData } = await axiosInstancePdv.patch('/profile/my/', {});

    try {
      const { data: companyData } = await axiosInstancePdv.get('/user/pdv_company/');

      const registerResponse = {
        token: response.token,
        isNewUser: false,
        user: userData as IPartnerUser,
        store: companyData.store as IStoreData,
        company: companyData.company as ICompanyResponse,
      };

      // Salvar dados do usuário no localStorage
      localStorage.setItem('partner_user', JSON.stringify(registerResponse.user));

      return registerResponse;
    } catch (error) {
      const registerResponse = {
        token: response.token,
        isNewUser: false,
        user: userData as IPartnerUser,
      };

      // Salvar dados do usuário no localStorage
      localStorage.setItem('partner_user', JSON.stringify(registerResponse.user));

      return registerResponse;
    }
  } catch (error: any) {
    // Capturar erros de validação no registro
    if (error.response?.status === 400 && error.response?.data) {
      const validationErrors = error.response.data;
      
      // Capturar erros de campos específicos
      Object.keys(validationErrors).forEach(field => {
        if (Array.isArray(validationErrors[field])) {
          partnerSentryConfig.captureValidationError(
            field,
            validationErrors[field],
            '/profile/'
          );
        }
      });
    }
    // Re-throw o erro para ser tratado pelo hook
    throw error;
  }
}
